import { useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { ensureIubendaScript } from '../utils/iubenda';
import CcpaOptOutIcon from './CcpaOptOutIcon';

// "Notice at collection" points at the privacy NOTICE, not the cookie policy.
const PRIVACY_NOTICE_URL = 'https://www.iubenda.com/privacy-policy/52362461';

/* WIRED EXPLICITLY, because iubenda's own wiring cannot reach this button.
   At the end of its init it binds the preferences modal to every
   `.iubenda-cs-preferences-link` in the document — but that runs once, before React has
   mounted this row, so our button is not in the document yet and never gets a handler.
   MEASURED, not assumed: clicking it opened nothing until this existed.
   Optional-chained throughout: if the consent script is blocked or still loading the click is
   inert rather than a TypeError, which is the correct failure for a footer control. */
const openPrivacyPreferences = () => window._iub?.cs?.api?.openPreferences?.();

// Surfaces that scroll forever. A footer only works where the page ENDS — on an infinite feed
// it either floats over the content or sits at a bottom the reader never reaches, and on the
// Workspace it lands underneath the docked composer, which is how it ended up invisible.
//
// Nothing is lost by omitting it here: the policy links a visitor must be able to reach are
// already on the paths where consent is actually taken — the sign-in form, the age gate and
// the subscribe modal all link Privacy and Terms directly — and the CCPA notice has to appear
// at or before the point of collection, which is those screens rather than this one.
// Session pages are fixed-height layouts sized to the viewport. The footer is 44px appended
// to <body>, so on those pages it was the ONLY thing making the document taller than the
// window — and that scroll is what made the fixed search bar look like it slid down into the
// video as you scrolled.
// /video is a fixed-height layout too — the row is lg:h-[calc(100vh-16px)] inside a
// lg:overflow-hidden container — and this row is 44px appended to <body>, so it was the only
// thing making that document taller than the window. The visible symptom was in the rail, not
// here: "Up Next" is lg:sticky, so 44px of document scroll dragged its heading up out of a
// container that clips it and the card list looked cut off at an arbitrary point.
//
// Reaching a video requires signing in, so it is not a first-touch surface: the policy links
// and the CCPA notice are already carried by the screens where consent is actually taken.
// Working surfaces, not documents: a legal footer under a tool reads as the end of the
// page and pushes the tool's own controls up off the fold. `/admin` covers the dashboard
// and the reel editor both.
// /upgrade-preview is a dev-only full-bleed dark review surface for the subscribe modal —
// a light legal row under it reads as a rendering bug, and nothing is collected there.
const NO_FOOTER_PATHS = ['/workspace', '/video', '/homework/session', '/lecture/session',
                         '/study/session', '/admin', '/upgrade-preview'];

const GlobalFooter = () => {
  const rowRef = useRef(null);
  const { pathname } = useLocation();
  const hidden = NO_FOOTER_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));

  useEffect(() => { ensureIubendaScript(); }, []);

  /* PUBLISH THE HEIGHT SO FULL-HEIGHT PAGES CAN SUBTRACT IT.
     This row is appended to <body> AFTER #root, so on any page claiming min-h-screen the
     document is a viewport PLUS this row — scrollable by ~44px with no content overflow. On
     iOS that is enough: a scrollable document lets Safari collapse and expand its toolbars on
     navigation, which reads as the whole screen dragging down. Confirmed 2026-09-22 by the
     pages that do NOT render this row (/workspace, /video) being the only ones that stay put.

     MEASURED, NOT ASSUMED. The row WRAPS on a phone — the links and iubenda's widget are wider
     than 393px — so its height is 44px on a desktop and more than that on a handset, and a
     hardcoded number would be wrong exactly where the bug shows up.

     Same pattern the rail already uses to publish --magi-sidebar, which this component
     consumes below. useLayoutEffect so the value lands before paint rather than shifting the
     page after it. */
  useLayoutEffect(() => {
    const el = rowRef.current;
    const root = document.documentElement;
    if (!el) { root.style.setProperty('--magi-footer-h', '0px'); return; }
    const publish = () => {
      // offsetHeight is 0 while display:none, which is exactly the value those pages want.
      root.style.setProperty('--magi-footer-h', `${el.offsetHeight}px`);
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    window.addEventListener('resize', publish);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', publish);
      root.style.setProperty('--magi-footer-h', '0px');
    };
  }, [hidden]);

  return createPortal(
    <div
      ref={rowRef}
      id="legal-footer"
      // WRAPS. On a phone the three policy links plus iubenda's widget are wider than the
      // screen — measured 585px of content in a 393px viewport, so every page carrying this
      // footer scrolled sideways by 192px. justify-between on a single line has nowhere to put
      // the overflow; wrapping puts the widget on its own row instead.
      className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 pr-6 py-3"
      style={{
        // HIDDEN, never unmounted. The row publishes --magi-footer-h from its measured height
        // and full-height pages subtract it; unmounting makes the variable jump to 0 and back
        // on every entry to and exit from a footer-less path, which relayouts every
        // .min-h-screen page underneath. display:none measures 0 without that churn.
        // (It was previously kept mounted to protect an adopted third-party node. That node is
        // gone — we render these links ourselves now — but the layout reason stands on its own.)
        display: hidden ? 'none' : undefined,
        // Portaled to <body>, so it does NOT inherit the offset <main> gets — without this
        // the first link sits underneath the fixed left rail and is unreadable. Same variable
        // the rail publishes, so it tracks collapsing and expanding.
        paddingLeft: 'calc(var(--magi-sidebar, 0px) + 1.5rem)',
      }}
    >
      <div className="flex items-center gap-4 text-sm">
        <a href="https://www.iubenda.com/privacy-policy/52362461"
           className="iubenda-noiframe iubenda-embed iubenda-noiframe"
           title="Privacy Policy">Privacy Policy</a>
        <a href="https://www.iubenda.com/terms-and-conditions/52362461"
           className="iubenda-noiframe iubenda-embed iubenda-noiframe"
           title="Terms and Conditions">Terms and Conditions</a>
        <a href="https://www.iubenda.com/privacy-policy/52362461/cookie-policy"
           className="iubenda-noiframe iubenda-embed iubenda-noiframe"
           title="Cookie Policy">Cookie Policy</a>
      </div>

      {/* OUR OWN MARKUP, NOT IUBENDA'S INJECTED WIDGET — and the two class names are the
          load-bearing part.

          iubenda used to append a `.iub__us-widget` to <body> on its own schedule and we moved
          it in here with a MutationObserver. Its visibility was then governed by two
          authorities at once — our stylesheet and its DOM position — racing a third-party
          fetch and a geolocation call. That race is what put "Notice at collection" on screen
          before the page arrived, four fixes running.

          core-en.js (1.107.0) offers a way out. `checkUsprFloatingPreferences` does:

              Te(".iubenda-cs-preferences-link", x) && Te(".iubenda-cs-uspr-link", x)

          where Te searches the WHOLE document, excluding only iubenda's own banner and iframe.
          If both already exist it leaves `usprPreferenceWidget` null, `shouldHaveUsprWidget()`
          returns false, and it never creates or appends the widget at all. Separately, at the
          end of its init it binds the preferences modal to EVERY
          `.iubenda-cs-preferences-link` in the document — ours included — via
          getElementsByClassName. So it is built to let a site own these links.

          BOTH classes or neither: supply only one and it builds a partial widget containing
          the half you left out. Pinned by tests/unit/test_legal_footer_reveal.py.

          The icon sits on the BUTTON, not the link, and that is deliberate: the rules above
          (`#legal-footer a[class*="iubenda"] svg`) hide svgs inside iubenda-classed anchors to
          strip its badge mark, and would hide this one too. A <button> is out of that
          selector's reach — and it is the honest element for something that opens a dialog. */}
      <div id="legal-footer-right" className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        <a href={PRIVACY_NOTICE_URL}
           className="iubenda-cs-uspr-link"
           title="Notice at collection">Notice at collection</a>
        <button type="button"
                className="iubenda-cs-preferences-link"
                onClick={openPrivacyPreferences}>
          Your Privacy Choices
          <CcpaOptOutIcon />
        </button>
      </div>
    </div>,
    document.body
  );
};

export default GlobalFooter;
